// frontend/src/hooks/games/useTetrisGame.ts

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BOARD_WIDTH,
  BOARD_HEIGHT,
  BASE_DROP_MS,
  MIN_DROP_MS,
  DROP_MS_PER_LEVEL,
  LINES_PER_LEVEL,
  LINE_CLEAR_SCORES,
  SOFT_DROP_SCORE,
  HARD_DROP_SCORE_PER_ROW,
} from '../../components/games/tetris/tetrisConstants';
import {
  TetrominoType,
  TETROMINO_SHAPES,
  createBagRandomizer,
  rotateMatrixClockwise,
} from '../../components/games/tetris/tetrominoes';

export type BoardCell = string | null; // null = boş, string = kilitli bloğun rengi
export type Board = BoardCell[][];

export interface ActivePiece {
  type: TetrominoType;
  shape: number[][];
  x: number;
  y: number;
}

function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_HEIGHT }, () => Array(BOARD_WIDTH).fill(null));
}

function spawnPiece(type: TetrominoType): ActivePiece {
  const shape = TETROMINO_SHAPES[type];
  const x = Math.floor((BOARD_WIDTH - shape.length) / 2);
  return { type, shape, x, y: -1 };
}

function canPlace(board: Board, shape: number[][], posX: number, posY: number): boolean {
  for (let r = 0; r < shape.length; r++) {
    for (let c = 0; c < shape[r].length; c++) {
      if (!shape[r][c]) continue;
      const boardX = posX + c;
      const boardY = posY + r;
      if (boardX < 0 || boardX >= BOARD_WIDTH || boardY >= BOARD_HEIGHT) return false;
      if (boardY >= 0 && board[boardY][boardX] !== null) return false;
    }
  }
  return true;
}

function lockPiece(board: Board, piece: ActivePiece): Board {
  const next = board.map((row) => [...row]);
  for (let r = 0; r < piece.shape.length; r++) {
    for (let c = 0; c < piece.shape[r].length; c++) {
      if (!piece.shape[r][c]) continue;
      const boardY = piece.y + r;
      const boardX = piece.x + c;
      if (boardY >= 0 && boardY < BOARD_HEIGHT && boardX >= 0 && boardX < BOARD_WIDTH) {
        next[boardY][boardX] = piece.type;
      }
    }
  }
  return next;
}

function clearLines(board: Board): { board: Board; linesCleared: number } {
  const remainingRows = board.filter((row) => row.some((cell) => cell === null));
  const linesCleared = BOARD_HEIGHT - remainingRows.length;
  const emptyRows = Array.from({ length: linesCleared }, () => Array(BOARD_WIDTH).fill(null));
  return { board: [...emptyRows, ...remainingRows], linesCleared };
}

function computeDropMs(level: number): number {
  return Math.max(MIN_DROP_MS, BASE_DROP_MS - (level - 1) * DROP_MS_PER_LEVEL);
}

interface EngineState {
  board: Board;
  current: ActivePiece;
  next: TetrominoType;
  score: number;
  lines: number;
  level: number;
  isPaused: boolean;
  isGameOver: boolean;
}

export function useTetrisGame() {
  const drawPieceRef = useRef(createBagRandomizer());

  const createInitialEngine = useCallback((): EngineState => {
    const draw = drawPieceRef.current;
    const firstType = draw();
    const nextType = draw();
    return {
      board: createEmptyBoard(),
      current: spawnPiece(firstType),
      next: nextType,
      score: 0,
      lines: 0,
      level: 1,
      isPaused: false,
      isGameOver: false,
    };
  }, []);

  const engineRef = useRef<EngineState>(createInitialEngine());
  const timeoutIdRef = useRef<number | null>(null);
  const loopRef = useRef<() => void>(() => {});
  const [renderVersion, setRenderVersion] = useState(0);

  const scheduleNext = useCallback((delayOverride?: number) => {
    const engine = engineRef.current;
    if (engine.isGameOver) return;
    const delay = delayOverride ?? (engine.isPaused ? 250 : computeDropMs(engine.level));
    timeoutIdRef.current = window.setTimeout(() => loopRef.current(), delay);
  }, []);

  const lockAndAdvance = useCallback((engine: EngineState) => {
    engine.board = lockPiece(engine.board, engine.current);
    const { board: clearedBoard, linesCleared } = clearLines(engine.board);
    engine.board = clearedBoard;

    if (linesCleared > 0) {
      engine.score += (LINE_CLEAR_SCORES[linesCleared] ?? 0) * engine.level;
      engine.lines += linesCleared;
      engine.level = Math.floor(engine.lines / LINES_PER_LEVEL) + 1;
    }

    const draw = drawPieceRef.current;
    const upcoming = spawnPiece(engine.next);
    engine.next = draw();

    if (!canPlace(engine.board, upcoming.shape, upcoming.x, upcoming.y)) {
      engine.isGameOver = true;
      engine.current = upcoming;
      return;
    }
    engine.current = upcoming;
  }, []);

  loopRef.current = () => {
    const engine = engineRef.current;
    if (engine.isGameOver) return;

    if (!engine.isPaused) {
      const piece = engine.current;
      if (canPlace(engine.board, piece.shape, piece.x, piece.y + 1)) {
        engine.current = { ...piece, y: piece.y + 1 };
      } else {
        lockAndAdvance(engine);
      }
    }

    setRenderVersion((v) => v + 1);
    if (!engine.isGameOver) scheduleNext();
  };

  useEffect(() => {
    scheduleNext(BASE_DROP_MS);
    return () => {
      if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveHorizontal = useCallback((dx: number) => {
    const engine = engineRef.current;
    if (engine.isGameOver || engine.isPaused) return;
    const piece = engine.current;
    if (canPlace(engine.board, piece.shape, piece.x + dx, piece.y)) {
      engine.current = { ...piece, x: piece.x + dx };
      setRenderVersion((v) => v + 1);
    }
  }, []);

  const rotate = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver || engine.isPaused) return;
    const piece = engine.current;
    const rotatedShape = rotateMatrixClockwise(piece.shape);
    // Basit duvar itme: doğrudan, +1, -1, +2, -2 x kaydırmalarını dener.
    for (const dx of [0, 1, -1, 2, -2]) {
      if (canPlace(engine.board, rotatedShape, piece.x + dx, piece.y)) {
        engine.current = { ...piece, shape: rotatedShape, x: piece.x + dx };
        setRenderVersion((v) => v + 1);
        return;
      }
    }
  }, []);

  const softDrop = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver || engine.isPaused) return;
    const piece = engine.current;
    if (canPlace(engine.board, piece.shape, piece.x, piece.y + 1)) {
      engine.current = { ...piece, y: piece.y + 1 };
      engine.score += SOFT_DROP_SCORE;
      setRenderVersion((v) => v + 1);
    }
  }, []);

  const hardDrop = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver || engine.isPaused) return;
    const piece = engine.current;
    let dropRows = 0;
    while (canPlace(engine.board, piece.shape, piece.x, piece.y + dropRows + 1)) {
      dropRows++;
    }
    engine.current = { ...piece, y: piece.y + dropRows };
    engine.score += dropRows * HARD_DROP_SCORE_PER_ROW;
    lockAndAdvance(engine);
    setRenderVersion((v) => v + 1);

    if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
    scheduleNext();
  }, [lockAndAdvance, scheduleNext]);

  const togglePause = useCallback(() => {
    const engine = engineRef.current;
    if (engine.isGameOver) return;
    engine.isPaused = !engine.isPaused;
    setRenderVersion((v) => v + 1);
  }, []);

  const restart = useCallback(() => {
    if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
    engineRef.current = createInitialEngine();
    setRenderVersion((v) => v + 1);
    scheduleNext(BASE_DROP_MS);
    // NOT: İleride restart burada yeni bir "round başladı" XP/istatistik event'i tetikleyebilir.
  }, [createInitialEngine, scheduleNext]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const key = e.key;
      if (['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', ' ', 'a', 'A', 'd', 'D', 's', 'S', 'w', 'W', 'p', 'P'].includes(key)) {
        e.preventDefault();
      }
      if (key === 'p' || key === 'P') { togglePause(); return; }
      if (key === 'ArrowLeft' || key === 'a' || key === 'A') moveHorizontal(-1);
      else if (key === 'ArrowRight' || key === 'd' || key === 'D') moveHorizontal(1);
      else if (key === 'ArrowUp' || key === 'w' || key === 'W') rotate();
      else if (key === 'ArrowDown' || key === 's' || key === 'S') softDrop();
      else if (key === ' ') hardDrop();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveHorizontal, rotate, softDrop, hardDrop, togglePause]);

  // NOT: İleride oyun bittiğinde (isGameOver true olduğunda) burada
  // XP/başarım/istatistik event'i tetiklenebilir. Şu an için implemente edilmedi.

  const engine = engineRef.current;
  void renderVersion;

  return {
    board: engine.board,
    current: engine.current,
    next: engine.next,
    score: engine.score,
    lines: engine.lines,
    level: engine.level,
    isPaused: engine.isPaused,
    isGameOver: engine.isGameOver,
    moveHorizontal,
    rotate,
    softDrop,
    hardDrop,
    togglePause,
    restart,
  };
}
